<template>
  <div
    class="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50 flex flex-col"
  >
    <!-- Background glow -->
    <div class="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div class="absolute -top-32 -left-24 h-72 w-72 rounded-full bg-sky-400/15 blur-3xl" />
      <div class="absolute -bottom-40 -right-24 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl" />
    </div>

    <!-- Top bar -->
    <header
      class="w-full border-b border-zinc-200/70 dark:border-zinc-800/70 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-sm"
    >
      <div class="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <div
            class="h-8 w-8 rounded-2xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center"
          >
            <span class="text-[18px] font-semibold text-sky-500">NG</span>
          </div>
          <div class="leading-tight">
            <p class="text-xs uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
              NexGridCo
            </p>
            <p class="text-sm font-medium text-zinc-900 dark:text-zinc-50">
              Legal & Privacy
            </p>
          </div>
        </div>

        <p
          class="hidden sm:inline-flex text-[11px] font-medium tracking-[0.18em] uppercase text-zinc-500 dark:text-zinc-400"
        >
          Data Protection Agreement
        </p>
      </div>
    </header>

    <!-- Content -->
    <main class="flex-1 flex items-stretch justify-center">
      <div class="w-full max-w-4xl mx-auto px-4 py-8 lg:py-10">
        <!-- Header + tabs -->
        <div class="mb-5 lg:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="text-[11px] uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
              Legal documents
            </p>
            <h1 class="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              Data Protection Agreement
            </h1>
            <p class="text-sm text-zinc-600 dark:text-zinc-400 max-w-xl">
              This DPA describes how NexGridCo handles, safeguards, and processes personal and
              project data in alignment with global data protection standards.
            </p>
          </div>

          <!-- Tabs -->
          <nav
            class="inline-flex items-center gap-1 rounded-full border border-zinc-200/80 dark:border-zinc-800/80 bg-white/80 dark:bg-zinc-900/80 px-1 py-1 text-xs"
          >
            <button
              type="button"
              class="rounded-full px-3 py-1 font-medium transition-colors"
              :class="isActive('terms')
                ? 'bg-sky-500 text-white shadow-sm'
                : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100/70 dark:hover:bg-zinc-800/70'"
              @click="go('terms')"
            >
              Terms
            </button>
            <button
              type="button"
              class="rounded-full px-3 py-1 font-medium transition-colors"
              :class="isActive('privacy')
                ? 'bg-sky-500 text-white shadow-sm'
                : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100/70 dark:hover:bg-zinc-800/70'"
              @click="go('privacy')"
            >
              Privacy
            </button>
            <button
              type="button"
              class="rounded-full px-3 py-1 font-medium transition-colors"
              :class="isActive('dpa')
                ? 'bg-sky-500 text-white shadow-sm'
                : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100/70 dark:hover:bg-zinc-800/70'"
              @click="go('dpa')"
            >
              DPA
            </button>
          </nav>
        </div>

        <!-- Card -->
        <section
          class="rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 bg-white/95 dark:bg-[#05060a]/95 shadow-2xl shadow-zinc-900/10 p-5 sm:p-6"
        >
          <div class="space-y-4 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300 max-h-[70vh] overflow-y-auto pr-1">
            <p>
              This Data Protection Agreement (“DPA”) outlines how NexGridCo handles, safeguards,
              and processes personal and project data in alignment with global data protection
              standards.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">1. Responsibilities</h2>
            <p>
              NexGridCo acts as a service provider processing data solely to operate the platform.
              You remain the owner and controller of your content and user information.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">2. Data Processing</h2>
            <p>We process data only for:</p>
            <ul class="list-disc list-inside space-y-1">
              <li>Providing platform features and tools.</li>
              <li>Maintaining security, availability, and backups.</li>
              <li>Storing palettes, projects, and user-created assets.</li>
            </ul>

            <h2 class="text-base sm:text-lg font-semibold mt-4">3. Confidentiality</h2>
            <p>
              All personnel with access to your data are bound by confidentiality and security
              obligations.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">4. Sub-Processors</h2>
            <p>
              NexGridCo may rely on trusted infrastructure providers (e.g., hosting, analytics, payment),
              each bound to comparable data protection obligations.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">5. Security Measures</h2>
            <p>
              NexGridCo implements administrative, technical, and physical safeguards appropriate to
              international data protection expectations.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">6. Data Deletion &amp; Export</h2>
            <p>
              Upon request or account closure, we will delete or export your data unless technical,
              legal, or operational requirements mandate retention.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">7. Incident Notification</h2>
            <p>
              In the unlikely event of a data incident, NexGridCo will notify affected users within a
              reasonable timeframe with relevant details and mitigation steps.
            </p>

            <h2 class="text-base sm:text-lg font-semibold mt-4">8. Updates to This Agreement</h2>
            <p>
              We may modify this DPA to reflect evolving global standards. Continued use of the Services
              constitutes acceptance of changes.
            </p>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const isActive = (name) => route.name === name
const go = (name) => {
  if (!isActive(name)) router.push({ name })
}
</script>
